import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, ChevronDown, LayoutGrid, Check, Plus, Archive } from "lucide-react";
import { useWorkspace } from "@/lib/workspace-context";

/**
 * Compact "My Interviews" switcher for the sidebar.
 * Auto-hides for users with only one interview (the concept is implicit at that
 * point). Shown only when the user has 2+ workspaces so switching is meaningful.
 */
export default function WorkspaceSwitcher() {
  const { workspaces, active, setActive, canCreateMore, archiveWorkspace } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [archivingId, setArchivingId] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const activeWorkspaces = workspaces.filter((w) => w.status !== "archived");
  // Hide entirely when the user has only one interview - no switching context needed.
  if (workspaces.length <= 1) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="workspace-switcher"
        className="w-full flex items-center gap-2.5 rounded-lg border border-zinc-800/70 bg-zinc-900/50 hover:bg-zinc-900/80 hover:border-zinc-700 px-3 py-2 text-left transition-colors"
      >
        <span className="h-6 w-6 rounded-md bg-amber-500/10 border border-amber-500/25 flex items-center justify-center flex-shrink-0">
          <Briefcase className="h-3 w-3 text-amber-400" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Preparing for</div>
          <div className="text-xs text-zinc-100 font-medium truncate" data-testid="workspace-switcher-name">
            {active?.name || "Loading…"}
          </div>
        </div>
        <ChevronDown className={`h-3 w-3 text-zinc-500 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          data-testid="workspace-switcher-menu"
          className="absolute left-0 right-0 top-full mt-1.5 rounded-lg border border-zinc-800 bg-zinc-950 shadow-2xl z-40 overflow-hidden animate-in"
        >
          <div className="max-h-60 overflow-y-auto py-1">
            {activeWorkspaces.map((w) => {
              const isActive = w.workspace_id === active?.workspace_id;
              const canArchive = activeWorkspaces.length > 1;
              const onArchiveClick = async (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!canArchive || archivingId) return;
                const label = w.name || "this interview";
                // eslint-disable-next-line no-alert
                if (!window.confirm(`Archive "${label}"? You can restore it anytime from All Interviews.`)) return;
                setArchivingId(w.workspace_id);
                try {
                  await archiveWorkspace(w.workspace_id);
                  setOpen(false);
                } finally {
                  setArchivingId(null);
                }
              };
              return (
                <div
                  key={w.workspace_id}
                  className={`group relative flex items-center hover:bg-zinc-900 transition-colors ${
                    isActive ? "text-amber-300" : "text-zinc-200"
                  }`}
                >
                  <button
                    onClick={() => { setActive(w.workspace_id); setOpen(false); }}
                    data-testid={`switcher-item-${w.workspace_id}`}
                    className="flex-1 min-w-0 flex items-center gap-2 pl-3 pr-2 py-2 text-left text-xs"
                  >
                    <Check className={`h-3 w-3 flex-shrink-0 ${isActive ? "text-amber-400" : "opacity-0"}`} />
                    <span className="flex-1 min-w-0">
                      <span className="block truncate font-medium">{w.name}</span>
                      {w.status !== "active" && <span className="text-[9px] uppercase tracking-widest text-zinc-500">{w.status}</span>}
                    </span>
                  </button>
                  <button
                    onClick={onArchiveClick}
                    disabled={!canArchive || archivingId === w.workspace_id}
                    data-testid={`switcher-archive-${w.workspace_id}`}
                    title={canArchive ? "Archive this interview" : "Keep at least one active interview"}
                    className={`opacity-0 group-hover:opacity-100 focus:opacity-100 mr-2 h-6 w-6 flex items-center justify-center rounded-md transition ${
                      canArchive
                        ? "text-zinc-500 hover:text-amber-300 hover:bg-amber-500/[0.08]"
                        : "text-zinc-700 cursor-not-allowed"
                    }`}
                  >
                    <Archive className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="border-t border-zinc-800/70 py-1">
            <Link
              to="/app/workspaces"
              onClick={() => setOpen(false)}
              data-testid="switcher-view-all"
              className="flex items-center gap-2 px-3 py-2 text-xs text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900 transition-colors"
            >
              <LayoutGrid className="h-3 w-3" /> View all interviews
            </Link>
            <Link
              to="/app/workspaces"
              onClick={() => setOpen(false)}
              data-testid="switcher-new-workspace"
              className="flex items-center gap-2 px-3 py-2 text-xs text-amber-300 hover:bg-amber-500/10 transition-colors"
            >
              <Plus className="h-3 w-3" /> New interview {!canCreateMore && <span className="ml-auto text-[9px] uppercase tracking-widest text-amber-400/70">Pro</span>}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
